/**
 * Reducer for RAPBox Management.
 * State shape mirrors the STATUS_PENDING / STATUS_SUCCESS / STATUS_FAILURE
 * convention used throughout the Admin module.
 */
import {
  STATUS_FAILURE,
  STATUS_PENDING,
  STATUS_SUCCESS,
} from "Common/constants/commonConstants";
import * as C from "./RAPBoxManagementConstants";

const initialState = {
  // ─── RAPBox list (infinite scroll) ───────────────────────────────────────
  fetchRAPBoxListStatus: STATUS_SUCCESS,
  fetchRAPBoxListErrMsg: "",
  rapBoxList: [],
  hasMore:               false,
  connectedRAPboxCount:  0,

  // ─── Selected device ─────────────────────────────────────────────────────
  selectedRAPBoxId: null,

  // ─── Device configuration ─────────────────────────────────────────────────
  fetchConfigStatus: STATUS_SUCCESS,
  fetchConfigErrMsg: "",
  rapBoxConfig: null,

  saveConfigStatus: STATUS_SUCCESS,
  saveConfigErrMsg: "",
  saveConfigSuccess: false,

  // ─── Health diagnostics ───────────────────────────────────────────────────
  fetchHealthStatus: STATUS_SUCCESS,
  fetchHealthErrMsg: "",
  rapBoxHealthList: [],

  // ─── Transferred files (File Retrieval section) ───────────────────────────
  fetchTransferredFilesStatus: STATUS_SUCCESS,
  fetchTransferredFilesErrMsg: "",
  transferredFilesList: [],
  hasMoreTransferredFiles: false,

  // ─── File retrieve request ────────────────────────────────────────────────
  fileRetrieveStatus:    STATUS_SUCCESS,
  fileRetrieveErrMsg:    "",
  fileRetrieveSucceeded: false,

  // ─── File download (datalake URL generation) ──────────────────────────────
  downloadingFile:    false,
  downloadFileFailed: false,
  downloadFileErrMsg: "",

  // ─── RAPBox RDS launch ────────────────────────────────────────────────────
  launchRDSStatus:    null,
  launchRDSErrMsg:    "",
  rdsProxyURL:        null,
  rdsSessionDone:     true,
  rdsSessionErrMsg:   "",
};

export default function RAPBoxManagementReducer(state = initialState, action) {
  switch (action.type) {
    // ── Fetch list ────────────────────────────────────────────────────────────
    case C.RAPBOX_FETCH_REQUEST:
      // Do NOT clear rapBoxList here: if offset > 0 we are appending; the list
      // clears only via RAPBOX_RESET_LIST (dispatched before every fresh search
      // or initial load).
      return { ...state, fetchRAPBoxListStatus: STATUS_PENDING, fetchRAPBoxListErrMsg: "" };

    case C.RAPBOX_FETCH_SUCCESS: {
      const { offset, data, hasMore, connectedRAPboxCount } = action.payload;
      return {
        ...state,
        fetchRAPBoxListStatus: STATUS_SUCCESS,
        // offset === 0 → fresh query (initial load / search reset): replace list
        // offset  >  0 → infinite-scroll trigger: append to existing list
        rapBoxList:           offset === 0 ? [...data] : [...state.rapBoxList, ...data],
        hasMore,
        connectedRAPboxCount,
      };
    }

    case C.RAPBOX_FETCH_ERROR:
      return {
        ...state,
        fetchRAPBoxListStatus: STATUS_FAILURE,
        fetchRAPBoxListErrMsg: C.GENERIC_ERROR_MESSAGE,
        hasMore: false,
      };

    // ── Reset device list (before fresh search or initial load) ──────────────
    case C.RAPBOX_RESET_LIST:
      return { ...state, rapBoxList: [], hasMore: false, fetchRAPBoxListErrMsg: "" };

    // ── Fetch config ──────────────────────────────────────────────────────────
    case C.RAPBOX_CONFIG_FETCH_REQUEST:
      return { ...state, fetchConfigStatus: STATUS_PENDING, fetchConfigErrMsg: "", rapBoxConfig: null };

    case C.RAPBOX_CONFIG_FETCH_SUCCESS:
      return { ...state, fetchConfigStatus: STATUS_SUCCESS, rapBoxConfig: action.payload };

    case C.RAPBOX_CONFIG_FETCH_ERROR:
      return { ...state, fetchConfigStatus: STATUS_FAILURE, fetchConfigErrMsg: C.GENERIC_ERROR_MESSAGE };

    // ── Save config ───────────────────────────────────────────────────────────
    case C.RAPBOX_CONFIG_SAVE_REQUEST:
      return { ...state, saveConfigStatus: STATUS_PENDING, saveConfigErrMsg: "", saveConfigSuccess: false };

    case C.RAPBOX_CONFIG_SAVE_SUCCESS:
      return { ...state, saveConfigStatus: STATUS_SUCCESS, saveConfigSuccess: true };

    case C.RAPBOX_CONFIG_SAVE_ERROR:
      return { ...state, saveConfigStatus: STATUS_FAILURE, saveConfigErrMsg: C.GENERIC_ERROR_MESSAGE, saveConfigSuccess: false };

    // ── Fetch health ──────────────────────────────────────────────────────────
    case C.RAPBOX_HEALTH_FETCH_REQUEST:
      return { ...state, fetchHealthStatus: STATUS_PENDING, fetchHealthErrMsg: "", rapBoxHealthList: [] };

    case C.RAPBOX_HEALTH_FETCH_SUCCESS:
      return { ...state, fetchHealthStatus: STATUS_SUCCESS, rapBoxHealthList: [...action.payload] };

    case C.RAPBOX_HEALTH_FETCH_ERROR:
      return { ...state, fetchHealthStatus: STATUS_FAILURE, fetchHealthErrMsg: C.GENERIC_ERROR_MESSAGE };

    // ── UI helpers ────────────────────────────────────────────────────────────
    case C.RAPBOX_SELECT_DEVICE:
      return { ...state, selectedRAPBoxId: action.payload, rapBoxConfig: null };

    case C.RAPBOX_CLOSE_MSG_POPUP:
      return { ...state, saveConfigStatus: STATUS_SUCCESS, saveConfigSuccess: false, saveConfigErrMsg: "" };

    case C.RAPBOX_RESET_STATE:
      return { ...initialState };

    // ── Fetch transferred files ───────────────────────────────────────────────
    case C.RAPBOX_TRANSFERRED_FILES_FETCH_REQUEST:
      return {
        ...state,
        fetchTransferredFilesStatus: STATUS_PENDING,
        fetchTransferredFilesErrMsg: "",
        // Clear the list only on a fresh load (page 0); keep existing rows when
        // appending subsequent pages so InfiniteScroll shows content continuously.
        transferredFilesList: action.pageNum === 0 ? [] : state.transferredFilesList,
      };

    case C.RAPBOX_TRANSFERRED_FILES_FETCH_SUCCESS: {
      const { data = [], hasMore = false, pageNum = 0 } = action.payload;
      return {
        ...state,
        fetchTransferredFilesStatus: STATUS_SUCCESS,
        // page 0 → replace list; page > 0 → append (infinite scroll)
        transferredFilesList:
          pageNum === 0 ? [...data] : [...state.transferredFilesList, ...data],
        hasMoreTransferredFiles: hasMore,
      };
    }

    case C.RAPBOX_TRANSFERRED_FILES_FETCH_ERROR:
      return {
        ...state,
        fetchTransferredFilesStatus: STATUS_FAILURE,
        fetchTransferredFilesErrMsg: C.GENERIC_ERROR_MESSAGE,
        hasMoreTransferredFiles: false,
      };

    // ── Request file retrieval ────────────────────────────────────────────────
    case C.RAPBOX_FILE_RETRIEVE_REQUEST:
      return { ...state, fileRetrieveStatus: STATUS_PENDING, fileRetrieveErrMsg: "", fileRetrieveSucceeded: false };

    case C.RAPBOX_FILE_RETRIEVE_SUCCESS: {
      const newFile = action.payload;
      return {
        ...state,
        fileRetrieveStatus: STATUS_SUCCESS,
        fileRetrieveSucceeded: true,
        transferredFilesList: newFile
          ? [newFile, ...state.transferredFilesList]
          : state.transferredFilesList,
      };
    }

    case C.RAPBOX_FILE_RETRIEVE_ERROR: {
      return { ...state, fileRetrieveStatus: STATUS_FAILURE, fileRetrieveErrMsg: action.payload || C.GENERIC_ERROR_MESSAGE, fileRetrieveSucceeded: false };
    }

    case C.RAPBOX_FILE_RETRIEVE_RESET:
      return { ...state, fileRetrieveStatus: STATUS_SUCCESS, fileRetrieveSucceeded: false };

    // ── File download ─────────────────────────────────────────────────────────
    case C.RAPBOX_FILE_DOWNLOAD_REQUEST:
      return { ...state, downloadingFile: true, downloadFileFailed: false, downloadFileErrMsg: "" };

    case C.RAPBOX_FILE_DOWNLOAD_SUCCESS:
      return { ...state, downloadingFile: false, downloadFileFailed: false };

    case C.RAPBOX_FILE_DOWNLOAD_ERROR:
      return { ...state, downloadingFile: false, downloadFileFailed: true, downloadFileErrMsg: action.payload || "" };

    case C.RAPBOX_FILE_DOWNLOAD_RESET:
      return { ...state, downloadingFile: false, downloadFileFailed: false, downloadFileErrMsg: "" };

    // ── Service Bus: update a single file's status ────────────────────────────
    case C.RAPBOX_TRANSFERRED_FILE_STATUS_UPDATE: {
      const { RequestID, IsSuccessful, BlobPath, UpdateDatetime, FailureDetails } = action.payload;
      return {
        ...state,
        transferredFilesList: state.transferredFilesList.map((f) => {
          if (f.topicListener && f.topicListener.subscription === RequestID) {
            return {
              ...f,
              status:        IsSuccessful ? "SUCCESS" : "FAILED",
              cloudFilePath: IsSuccessful ? (BlobPath || f.cloudFilePath) : f.cloudFilePath,
              insertDatetime: UpdateDatetime || f.insertDatetime,
              failureMessage: IsSuccessful ? f.failureMessage : (FailureDetails || f.failureMessage),
              topicListener: null,
            };
          }
          return f;
        }),
      };
    }

    // ── Launch RAPBox RDS ──────────────────────────────────────────────────────
    case C.RAPBOX_LAUNCH_RDS_REQUEST:
      return { ...state, launchRDSStatus: STATUS_PENDING, launchRDSErrMsg: "", rdsProxyURL: null, rdsSessionDone: false, rdsSessionErrMsg: "" };

    case C.RAPBOX_LAUNCH_RDS_SUCCESS:
      return { ...state, launchRDSStatus: STATUS_SUCCESS, rdsProxyURL: action.payload };

    case C.RAPBOX_LAUNCH_RDS_ERROR:
      return { ...state, launchRDSStatus: STATUS_FAILURE, launchRDSErrMsg: action.payload || C.GENERIC_ERROR_MESSAGE, rdsProxyURL: null };

    case C.RAPBOX_LAUNCH_RDS_RESET:
      return { ...state, launchRDSStatus: null, launchRDSErrMsg: "", rdsProxyURL: null, rdsSessionDone: true, rdsSessionErrMsg: "" };

    case C.RAPBOX_LAUNCH_RDS_SESSION_COMPLETE:
      return { ...state, rdsSessionDone: true, rdsSessionErrMsg: "" };

    case C.RAPBOX_LAUNCH_RDS_SESSION_ERROR:
      return { ...state, rdsSessionDone: true, rdsSessionErrMsg: action.payload };

    default:
      return state;
  }
}
